import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MobileOptimizationService {
  private currentBreakpoint: string = 'desktop';
  private static readonly GIF_URL_REGEX = /\.gif(\?|#|$)/i;

  constructor(
    private breakpointObserver: BreakpointObserver,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeBreakpointObserver();
  }

  private initializeBreakpointObserver(): void {
    // Only observe breakpoints in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.breakpointObserver.observe([
        Breakpoints.Handset,
        Breakpoints.Tablet,
        Breakpoints.Web
      ]).subscribe(result => {
        if (result.matches) {
          if (result.breakpoints[Breakpoints.Handset]) {
            this.currentBreakpoint = 'mobile';
          } else if (result.breakpoints[Breakpoints.Tablet]) {
            this.currentBreakpoint = 'tablet';
          } else if (result.breakpoints[Breakpoints.Web]) {
            this.currentBreakpoint = 'desktop';
          }
        }
      });
    } else {
      // For SSR, default to desktop for better quality in server-rendered HTML
      this.currentBreakpoint = 'desktop';
    }
  }

  getCurrentBreakpoint(): string {
    return this.currentBreakpoint;
  }

  /**
   * Get optimized image URL using AWS Serverless Image Handler
   * @param originalUrl - Original image URL
   * @param width - Desired width
   * @param height - Desired height
   * @param quality - Image quality (optional, if not provided will use breakpoint-based quality)
   * @param format - Image format (optional, defaults to webp)
   * @returns AWS Serverless Image Handler transformation URL
   */
  getOptimizedImageUrl(originalUrl: string, width?: number, height?: number, quality?: number, format?: string, fit?: 'contain' | 'cover' | 'inside', backgroundColor?: string): string {
    if (!originalUrl) return originalUrl;
    if (MobileOptimizationService.GIF_URL_REGEX.test(originalUrl)) {
      if (originalUrl.startsWith(environment.s3Config.bucketUrl)) {
        try {
          return this.buildImageHandlerUrl(originalUrl, width, height, undefined, 'gif', fit);
        } catch (error) {
          return originalUrl;
        }
      }
      return originalUrl;
    }

    try {
      const urlParts = this.parseUrl(originalUrl);
      const isTopFansCDN = urlParts.hostname.includes('topfans') || urlParts.hostname.includes('cloudfront.net');
      
      if (isTopFansCDN) {
        // Use breakpoint-based quality if no quality is provided
        const finalQuality = quality || this.getBreakpointBasedQuality();
        return this.buildImageHandlerUrl(originalUrl, width, height, finalQuality, format, fit, backgroundColor);
      } else {
        return originalUrl;
      }
    } catch (error) {
      return originalUrl;
    }
  }

  /**
   * Get quality based on current breakpoint
   * For SSR, uses a conservative mobile-friendly quality
   * @returns Quality value (0-100)
   */
  private getBreakpointBasedQuality(): number {
    // For SSR, use high quality for better server-rendered images
    if (!isPlatformBrowser(this.platformId)) {
      return 65; // High quality for SSR
    }

    switch (this.currentBreakpoint) {
      case 'mobile':
        return 60;  // Lower quality for mobile to reduce file size (was 65)
      case 'tablet':
        return 70;  // Good quality for tablet (was 75)
      case 'desktop':
      default:
        return 85;  // High quality for desktop (was 90)
    }
  }

  /**
   * Get optimized poster URL for video elements
   * Applies aggressive compression for poster images
   * @param originalUrl - Original poster URL
   * @param width - Desired width (optional)
   * @param height - Desired height (optional)
   * @returns Optimized poster URL
   */
  getOptimizedPosterUrl(originalUrl: string, width?: number, height?: number, fit?: 'contain' | 'cover' | 'inside', backgroundColor?: string): string {
    if (!originalUrl) return originalUrl;

    try {
      const urlParts = this.parseUrl(originalUrl);
      const isTopFansCDN = urlParts.hostname.includes('topfans') || urlParts.hostname.includes('cloudfront.net');
      
      if (isTopFansCDN) {
        // Use aggressive quality for posters (they're static, so lower quality is acceptable)
        // Quality: mobile=55, tablet=65, desktop=75 (lower than regular images)
        const posterQuality = !isPlatformBrowser(this.platformId) 
          ? 60 
          : (this.currentBreakpoint === 'mobile' ? 55 : this.currentBreakpoint === 'tablet' ? 65 : 75);
        
        return this.buildImageHandlerUrl(originalUrl, width, height, posterQuality, 'webp', fit, backgroundColor);
      } else {
        return originalUrl;
      }
    } catch (error) {
      return originalUrl;
    }
  }

  private parseUrl(urlString: string): { hostname: string; pathname: string } {
    try {
      const parsed = new URL(urlString);
      return {
        hostname: parsed.hostname || '',
        pathname: parsed.pathname || ''
      };
    } catch (e) {
      const match = urlString.match(/^https?:\/\/([^\/]+)(.*)$/);
      if (match) {
        return {
          hostname: match[1],
          pathname: match[2] || '/'
        };
      }
      throw new Error('Invalid URL');
    }
  }

  private buildImageHandlerUrl(originalUrl: string, width?: number, height?: number, quality?: number, format?: string, fit?: 'contain' | 'cover' | 'inside', backgroundColor?: string): string {
    const urlParts = this.parseUrl(originalUrl);
    const pathParts = urlParts.pathname.split('/').filter(part => part.length > 0);
    const decodedParts = pathParts.map(part => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    });
    const key = decodedParts.join('/');
    
    const imageHandlerPayload: any = {
      bucket: environment.s3Config.bucketName,
      key: key,
      edits: {
        toFormat: format || 'webp'
      }
    };
    // Set format-specific quality
    const finalQuality = quality || 75;
    if (format === 'webp' || !format) {
      imageHandlerPayload.edits.webp = { quality: finalQuality };
    } else if (format === 'jpeg' || format === 'jpg') {
      imageHandlerPayload.edits.jpeg = { quality: finalQuality };
    } else if (format === 'png') {
      imageHandlerPayload.edits.png = { quality: finalQuality };
    }
    // Add resize operation if dimensions provided
    if (width && height) {
      imageHandlerPayload.edits.resize = {
        width: width,
        height: height,
        fit: (fit === 'contain' || fit === 'cover' || fit === 'inside') ? fit : 'cover'
      };
    }
    // Optional background color (hex) -> rgba for flatten and contain background
    const rgba = this.hexToRgba(backgroundColor);
    if (rgba) {
      imageHandlerPayload.edits.flatten = { background: rgba };
      if (imageHandlerPayload.edits.resize && imageHandlerPayload.edits.resize.fit === 'contain') {
        imageHandlerPayload.edits.resize.background = rgba;
      }
    }
    const base64Payload = btoa(JSON.stringify(imageHandlerPayload));
    return `${environment.s3Config.bucketUrl}${base64Payload}`;
  }

  private hexToRgba(hex?: string): { r: number; g: number; b: number; alpha: number } | null {
    if (!hex) return null;
    try {
      let h = hex.trim();
      if (h.startsWith('#')) h = h.slice(1);
      if (h.length === 3) {
        h = h.split('').map(c => c + c).join('');
      }
      const num = parseInt(h, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
      return { r, g, b, alpha: 1 };
    } catch {
      return null;
    }
  }
}